import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { makeStyles } from "tss-react/mui";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Skeleton from "@mui/material/Skeleton";

import CloseIcon from "@mui/icons-material/Close";
import BackspaceIcon from "@mui/icons-material/Backspace";

import { clearRunningProcesses, completeRunningProcesses } from "../../../store/actions";
import { KMKDrawer } from "../../Common";

import WorkspaceClass from "../../../service/workspace";

const useStyles = makeStyles()((theme) => ({
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
  },
  heading: {
    color: "#DF672A",
    fontWeight: "bold",
    fontSize: "1rem",
  },
  closeIcon: {
    color: "buttontext",
    padding: 0,
  },
  container: {
    height: "100%",
    padding: "25px 30px",
  },
  errorText: {
    marginBottom: 0,
    color: theme.palette.error.main,
    cursor: "pointer",
    "& span": {
      borderBottom: "1px solid",
    },
  },
  warningText: {
    marginBottom: 0,
    color: "orange",
    cursor: "pointer",
    "& span": {
      borderBottom: "1px solid",
    },
  },
  infoText: {
    marginBottom: 0,
    color: theme.palette.info.main,
  },
  successText: {
    marginBottom: 0,
    color: theme.palette.success.main,
  },
  TableHead: {
    "& th": {
      fontWeight: 600,
    },
  },
  TableContainer: {
    maxHeight: "77vh",
    overflow: "auto",
    "&::-webkit-scrollbar-track": {
      marginTop: "36px",
    },
  },
}));

const ResentProgresses = ({ onClose }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { Workspace_id } = useParams();

  const runningProcesses = useSelector((state) => state.runningProcesses)?.filter(
    (process) => process.workspace === Workspace_id
  );

  const [loading, setLoading] = useState(true);
  let interval = useRef(null); //interval

  const runningProcessList = useMemo(() => {
    return runningProcesses
      .filter((process) => process.isRunning && !process.runningProcesses)
      .map(({ historyId }) => historyId);
  }, [runningProcesses]);

  const clearRunningImport = (historyId) => {
    dispatch(clearRunningProcesses({ historyId }));
  };

  useEffect(() => {
    let isCancelled = false; //stop api execution

    const fetchData = () => {
      let runInterval = false;

      if (runningProcessList.length) {
        const Workspace = new WorkspaceClass();

        Workspace.getUploadHistory(Workspace_id, runningProcessList).then((response) => {
          if (isCancelled) return;

          if (response?.success) {
            const data = response.data;

            runningProcesses.forEach((prevProcess) => {
              if (runningProcessList.includes(prevProcess.historyId)) {
                const process = data.find((proc) => proc.FileUploadHistory_id === prevProcess.historyId);

                if (!process) return;
                if (process.Status === "Running") {
                  runInterval = true;
                  return;
                }

                //Updating status to done if upload completed
                dispatch(
                  completeRunningProcesses({
                    ...prevProcess,
                    success: process.Status === "Success",
                    warning: process.Status === "Warning",
                    error: process.StatusText,
                  })
                );
              }
            });

            //Clear/Start interval
            if (!runInterval && interval.current) {
              clearInterval(interval.current);
            }
          }
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    };

    fetchData();
    interval.current = setInterval(fetchData, 5000);

    //Clean-up
    return () => {
      isCancelled = true;
      clearInterval(interval.current);
    };
  }, [Workspace_id, dispatch, runningProcessList, runningProcesses]);

  //Clean-up
  useEffect(() => {
    return () => {
      clearInterval(interval.current);
    };
  }, []);

  return (
    <KMKDrawer anchor="right" open={true} onClose={onClose} width={250}>
      <Toolbar />
      <Box className={classes.container} role="presentation">
        <Box className={classes.header}>
          <Typography className={classes.heading} variant="h6" gutterBottom component="div">
            Recent Imports
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        </Box>

        <TableContainer className={classes.TableContainer} component={Paper}>
          <Table stickyHeader sx={{ minWidth: 360 }} size="small">
            <TableHead className={classes.TableHead}>
              <TableRow>
                <TableCell align="left">Schema</TableCell>
                <TableCell align="left">Table</TableCell>
                <TableCell align="left">Status</TableCell>
                <TableCell width="50px" align="left"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runningProcesses.length ? (
                <>
                  {runningProcesses.map((process) => (
                    <TableRow key={process.historyId} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell component="th" scope="row">
                        {process.schemaName}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {process.tableName}
                      </TableCell>
                      <TableCell align="left">
                        {loading && !process.success ? (
                          <Skeleton variant="rectangular" width={75} height={20} />
                        ) : (
                          <>
                            {process.isRunning ? (
                              <Typography className={classes.infoText} variant="inherit">
                                {process.type === "local"
                                  ? `Processing ${process.runningProcesses}%`
                                  : "Import in progress"}
                              </Typography>
                            ) : process.success ? (
                              <Typography className={classes.successText} variant="inherit">
                                Success
                              </Typography>
                            ) : process.warning ? (
                              <Tooltip
                                className={classes.warningText}
                                title={
                                  <pre
                                    style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", wordWrap: "break-word" }}
                                  >
                                    {process.error}
                                  </pre>
                                }
                              >
                                <Typography variant="inherit">
                                  <span>Warning</span>
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Tooltip className={classes.errorText} title={process.error}>
                                <Typography variant="inherit">
                                  <span>Failed</span>
                                </Typography>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </TableCell>
                      <TableCell align="left">
                        <Tooltip placement="top" title={process.isRunning ? "" : "Remove"}>
                          <IconButton
                            color="error"
                            disabled={process.isRunning}
                            aria-label="close"
                            onClick={() => clearRunningImport(process.historyId)}
                          >
                            <BackspaceIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              ) : (
                <TableRow key="no-data" sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell colSpan={3} component="td" scope="row" align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </KMKDrawer>
  );
};

export default ResentProgresses;
